import { useQueryParams } from 'modules/routing/useQueryParams';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as yup from 'yup';
import { isEmpty, omit } from 'lodash/fp';
import { toQueryObject } from 'modules/routing/query-string';
import { TokenLaunchPeriods } from '../Wallets/usePageParams';

const schema = {
  status: yup.string().oneOf(['buy', 'sell']),
  poolCreatedAt: yup
    .number()
    .oneOf(Object.values(TokenLaunchPeriods) as number[]),
  address: yup.string(),
  walletAddress: yup.string(),
  minMc: yup.number(),
  maxMc: yup.number(),
  minVolume: yup.number(),
  maxVolume: yup.number(),
  minLiquidity: yup.number(),
  maxLiquidity: yup.number(),
};

export function usePageParams() {
  return useQueryParams(schema);
}

export type FilterState = yup.TypeFromShape<typeof schema, any>;

export const CACHE_KEY = 'smartSwapsFilter-v4';

export function useSmartSwapsSearchFilters() {
  const [filtersState, setFilters] = usePageParams();

  const setFiltersWithCache = useFiltersWithCache(setFilters, {}, (queryObj) =>
    isEmpty(omit(['searchDefaultOpen'], queryObj))
  );

  return { filtersState, setFilters: setFiltersWithCache };
}

function useFiltersWithCache(
  setFilters: (filters: FilterState) => void,
  defaults: Partial<FilterState> = {},
  shouldApplyCache: (filters: FilterState) => boolean = () => true
) {
  const location = useLocation();

  useEffect(() => {
    if (
      shouldApplyCache(toQueryObject(location.search) as unknown as FilterState)
    ) {
      const fromCache = window.localStorage.getItem(CACHE_KEY);

      setFilters(fromCache ? { ...JSON.parse(fromCache) } : defaults);
    }
  }, [location.search]);

  return function cacheAndSetFilters(filters: FilterState) {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(filters));
    setFilters(filters);
  };
}
